/**
 * ResearchRunsHeaderButton — opens the runs panel from the page header.
 *
 * Auto-hides when there are zero runs so the toolbar stays uncluttered.
 * Renders a single static Clock icon plus a count badge: active runs in
 * primary tint, unread terminals in destructive tint when no active runs
 * are in flight. No animated spinner — the run state is pulled from the
 * backend registry, not inferred client-side.
 */
import { useTranslation } from "react-i18next"

import { Clock } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { useAgentRunCounts } from "../../hooks/use-agent-runs-query"

interface ResearchRunsHeaderButtonProps {
  onOpen: () => void
}

export function ResearchRunsHeaderButton({ onOpen }: ResearchRunsHeaderButtonProps) {
  const { t } = useTranslation()
  const { active, unread, total } = useAgentRunCounts()

  if (total === 0) return null

  // Badge precedence: active count wins over unread. Active dominates
  // because it's the live signal; once everything is terminal we surface
  // the "needs review" count instead.
  const badgeCount = active > 0 ? active : unread
  const badgeTone =
    active > 0
      ? "border border-foreground bg-primary text-primary-foreground"
      : "border border-foreground bg-destructive text-destructive-foreground"

  let tooltipLabel: string
  if (active > 0) {
    tooltipLabel = t("deepExplorer.runs_active_button", {
      count: active,
      defaultValue: active === 1 ? "Agent running" : `${active} agent runs`,
    })
  } else if (unread > 0) {
    tooltipLabel = t("deepExplorer.runs_unread_tooltip", {
      count: unread,
      defaultValue: unread === 1 ? "1 run to review" : `${unread} runs to review`,
    })
  } else {
    tooltipLabel = t("deepExplorer.runs_open_panel", { defaultValue: "Open agent runs" })
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          aria-label={tooltipLabel}
          size="icon"
          variant="ghost"
          className={cn(
            buttonVariants({ size: "icon", variant: "icon", className: "bg-background" }),
            "relative size-9 rounded-full"
          )}
          onClick={onOpen}
        >
          <Clock className="size-5" />
          {badgeCount > 0 && (
            <span
              className={cn(
                "absolute -top-0.5 -right-0.5 min-w-4 rounded-full px-1 text-[10px] leading-4 tabular-nums",
                badgeTone
              )}
            >
              {badgeCount}
            </span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltipLabel}</TooltipContent>
    </Tooltip>
  )
}
